import React, {useState, useEffect, useRef} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import { SPECIALIST_FILTER_SPECIALITY_KEY } from '@/constants';
import {
  appSpecializations,
  allSpecialists,
  allSpecialistsNextPage,
  patientDashboardLoadingStatus,
  patientDashboardSuccessState,
  patientDashboardFailureState,
  dashboardSearchTerm,
  specialistFilterOptions,
  hospitalBookingModalState,
  recentCasesSelector,
} from '@/services/redux/selectors';
import {
  getAllSpecialistsRequest,
  resetPatientDashboardMessage,
  resetSpecialistList,
  specialistBookingRequest,
  selectHospitalSpecialistRequest,
  setDashboardSearchTerm,
  setHospitalBookingModalState,
  updateHospitalBookingModalState,
  clearHospitalBookingModalState,
  addFavouriteDoctorRequest,
  removeFavouriteDoctorRequest,
  getRecentCasesRequest,
} from '@/services/redux/slices';

import {Doctor, FilterItemResponse, GetSpecialistListRequestPayload, SelectInputitem, Specialization} from '@/interface';
import {Button, Icon, SelectInput, Text, TextInput} from '@/ui-kits';
import {SpecialistCard} from '@/components';
import {HospitalBookingModal} from '@/components/specialist-screen/HospitalBookingModal';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {useToast, useInfiniteScroll} from '@/hooks';
import { cn } from '@/utils/common_functions';
import {Routes} from '@/navigation/Routes';

export const SpecializationScreen: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const navigate = useNavigate();
  const location = useLocation();
  const {showToast} = useToast();
  const dispatch = useDispatch();

  // ==========================
  // selectors
  // ==========================
  const specialization = useSelector(appSpecializations);
  const specialistList = useSelector(allSpecialists);
  const nextPage = useSelector(allSpecialistsNextPage);
  const searchTerm = useSelector(dashboardSearchTerm);
  const patientDashboardSuccess = useSelector(patientDashboardSuccessState) as SuccessCodes;
  const patientDashboardFailure = useSelector(patientDashboardFailureState) as ErrorCodes;
  const isLoading = useSelector(patientDashboardLoadingStatus);
  const filterOptions = useSelector(specialistFilterOptions);
  const savedModalState = useSelector(hospitalBookingModalState);
  const recentCases = useSelector(recentCasesSelector);

  // ==========================
  // states
  // ==========================
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
  const [showAllCategories, setShowAllCategories] = useState<boolean>(false);

  // Search state for mobile
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Filter states - store selected filter ID
  const [filterExperience, setFilterExperience] = useState<FilterItemResponse | null>(null);
  const [filterCharges, setFilterCharges] = useState<FilterItemResponse | null>(null);
  const [filterRatings, setFilterRatings] = useState<FilterItemResponse | null>(null);

  // Applied filter states - store selected filter ID
  const [appliedExperience, setAppliedExperience] = useState<FilterItemResponse | null>(null);
  const [appliedCharges, setAppliedCharges] = useState<FilterItemResponse | null>(null);
  const [appliedRatings, setAppliedRatings] = useState<FilterItemResponse | null>(null);

  // Refs for tracking mount and filter restoration
  const isInitialMount = useRef<boolean>(true);
  const [filtersRestored, setFiltersRestored] = useState<boolean>(false);

  // Hospital booking modal states
  const [isHospitalBookingModalOpen, setIsHospitalBookingModalOpen] = useState<boolean>(false);
  const [selectedHospital, setSelectedHospital] = useState<Doctor | null>(null);

  // Check if any filters are applied
  const hasActiveFilters = selectedCategory || appliedExperience || appliedCharges || appliedRatings;
  const displayedCategories = showAllCategories ? specialization : specialization.slice(0, 6);

  // Load filters from localStorage on mount
  useEffect(() => {
    if (filtersRestored || !filterOptions || !filterOptions.experienceFilters || filterOptions.experienceFilters.length === 0) {
      return; // Wait for filterOptions to be loaded or already restored
    }

    const savedFilters = localStorage.getItem(SPECIALIST_FILTER_SPECIALITY_KEY);
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        if (parsed.appliedExperience) {
          const expFilter = filterOptions.experienceFilters.find(f => f.id === parsed.appliedExperience.id);
          if (expFilter) {
            setAppliedExperience(expFilter as FilterItemResponse);
            setFilterExperience(expFilter as FilterItemResponse);
          }
        }
        if (parsed.appliedCharges) {
          const chargesFilter = filterOptions.chargesFilters.find(f => f.id === parsed.appliedCharges.id);
          if (chargesFilter) {
            setAppliedCharges(chargesFilter as FilterItemResponse);
            setFilterCharges(chargesFilter as FilterItemResponse);
          }
        }
        if (parsed.appliedRatings) {
          const ratingsFilter = filterOptions.ratingFilters.find(f => f.id === parsed.appliedRatings.id);
          if (ratingsFilter) {
            setAppliedRatings(ratingsFilter as FilterItemResponse);
            setFilterRatings(ratingsFilter as FilterItemResponse);
          }
        }
        if (parsed.selectedCategory) {
          setSelectedCategory(parsed.selectedCategory);
        }
        setFiltersRestored(true);
      } catch (error) {
        setFiltersRestored(true);
      }
    } else {
      setFiltersRestored(true);
    }
  }, [filterOptions, filtersRestored]);

  // ==========================
  // functions
  // ==========================
  const handleSelectDoctor = (id: number | null) => {
    setSelectedDoctorId(id);
  };

  // ================
  // Booking Handlers
  // ================
  const handleBookNow = (doctor: Doctor) => {
    // For hospital specialists, use selectHospitalSpecialist flow
    if (doctor.is_hospital_specialist) {
      dispatch(selectHospitalSpecialistRequest({id: doctor.id}));
    } else {
      // For regular specialists, use regular booking flow
      dispatch(specialistBookingRequest({id: doctor.id}));
    }
  };

  const handleHospitalBooking = (doctor: Doctor) => {
    setSelectedHospital(doctor);
    setIsHospitalBookingModalOpen(true);
    dispatch(
      setHospitalBookingModalState({
        returnPath: Routes.SPECIALISTS,
        hospitalId: doctor.id,
        hospitalName: doctor.name,
        specializations: doctor.available_specializations || [],
        selectedSpecialization: null,
        isStep2: false,
        dashboardSearch: searchTerm,
      }),
    );
  };

  const handleCloseHospitalModal = () => {
    setIsHospitalBookingModalOpen(false);
    setSelectedHospital(null);
    dispatch(clearHospitalBookingModalState());
  };

  // ================
  // Favorite Toggle Handler
  // ================
  const handleFavoriteToggle = (doctorId: number) => {
    const doctor = specialistList.find(s => s.id === doctorId);
    if (doctor?.is_favourite) {
      dispatch(removeFavouriteDoctorRequest({specialist_id: doctorId}));
    } else {
      dispatch(addFavouriteDoctorRequest({specialist_id: doctorId}));
    }
  };

  // ================
  // Get Last Consulted Date for a Doctor
  // ================
  const getLastConsultedDate = (doctorName: string): string | null => {
    const recentCase = recentCases.find(
      (rc: any) => rc.doctor_name?.toLowerCase() === doctorName?.toLowerCase()
    );
    return recentCase?.close_at || null;
  };

  const handleApplyFilters = () => {
    setAppliedExperience(filterExperience);
    setAppliedCharges(filterCharges);
    setAppliedRatings(filterRatings);

    // Save filters to localStorage
    localStorage.setItem(
      SPECIALIST_FILTER_SPECIALITY_KEY,
      JSON.stringify({
        appliedExperience: filterExperience,
        appliedCharges: filterCharges,
        appliedRatings: filterRatings,
        selectedCategory: selectedCategory,
      }),
    );
  };

  const handleResetFilters = () => {
    setFilterExperience(null);
    setFilterCharges(null);
    setFilterRatings(null);
    setSelectedCategory(null);
    setAppliedExperience(null);
    setAppliedCharges(null);
    setAppliedRatings(null);

    // Clear saved filters from localStorage
    localStorage.removeItem(SPECIALIST_FILTER_SPECIALITY_KEY);
  };

  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategory(categoryId);
  };

  /**
   * Build filter params for API requests
   */
  const buildFilterParams = (page: number): GetSpecialistListRequestPayload => {
    const params: GetSpecialistListRequestPayload = {
      page: page,
    };

    // Use applied filters (not the temporary filter states)
    if (appliedExperience) {
      params.min_experience = appliedExperience.min;
      params.max_experience = appliedExperience.max;
    }
    if (appliedCharges) {
      params.min_charges = appliedCharges.min;
      params.max_charges = appliedCharges.max;
    }
    if (appliedRatings) {
      params.min_rating = appliedRatings.min;
      params.max_rating = appliedRatings.max;
    }

    if (selectedCategory) {
      params.category = selectedCategory;
    }

    if (searchTerm) {
      params.search = searchTerm;
    }

    return params;
  };

  /**
   * Infinite scroll hook
   */
  const observerTarget = useInfiniteScroll({
    onLoadMore: () => {
      if (nextPage) {
        dispatch(getAllSpecialistsRequest(buildFilterParams(nextPage)));
      }
    },
    hasMore: nextPage !== null,
    isLoading: isLoading,
    rootMargin: '200px',
  });

  // ==========================
  // side effects
  // ==========================
  // Initial load on component mount
  useEffect(() => {
    if (!filtersRestored || !isInitialMount.current) {
      return; // Wait for filters to be restored or already loaded
    }

    // Reset and load specialists on first mount
    dispatch(resetSpecialistList());
    dispatch(getAllSpecialistsRequest(buildFilterParams(1)));
    dispatch(getRecentCasesRequest());
    isInitialMount.current = false;
  }, [filtersRestored, dispatch]);

  // Sync local search term with Redux
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Restore hospital booking modal when returning from specialist profile (back button)
  useEffect(() => {
    if (savedModalState && savedModalState.returnPath === Routes.SPECIALISTS && location.pathname === Routes.SPECIALISTS) {
      setSelectedHospital({
        id: savedModalState.hospitalId,
        name: savedModalState.hospitalName,
        available_specializations: savedModalState.specializations,
      } as Doctor);
      setIsHospitalBookingModalOpen(true);

      const searchTerm = savedModalState.dashboardSearch || '';
      setLocalSearchTerm(searchTerm);
      dispatch(setDashboardSearchTerm(searchTerm));
    }
  }, [savedModalState, location.pathname]);

  // Handle search change for mobile
  const handleSearchChange = (value: string) => {
    setLocalSearchTerm(value);
    dispatch(setDashboardSearchTerm(value));
  };

  // Reload when filters change
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    dispatch(resetSpecialistList());
    dispatch(getAllSpecialistsRequest(buildFilterParams(1)));
  }, [searchTerm, selectedCategory, appliedExperience, appliedCharges, appliedRatings, dispatch]);

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    const currentFilters = localStorage.getItem(SPECIALIST_FILTER_SPECIALITY_KEY);
    const parsed = currentFilters ? JSON.parse(currentFilters) : {};
    localStorage.setItem(
      SPECIALIST_FILTER_SPECIALITY_KEY,
      JSON.stringify({
        ...parsed,
        selectedCategory: selectedCategory,
      }),
    );
  }, [selectedCategory]);

  // Toast messages
  useEffect(() => {
    if (patientDashboardSuccess) {
      if (patientDashboardSuccess !== 'S-10032' && patientDashboardSuccess !== 'S-10096') {
        showToast(getSuccessMessage(patientDashboardSuccess), 'success');
      }
      if (patientDashboardSuccess === 'S-10100' || patientDashboardSuccess === 'S-10097') {
        navigate(Routes.DASHBOARD);
      }
      dispatch(resetPatientDashboardMessage());
    }
    if (patientDashboardFailure && patientDashboardFailure !== 'E-10076') {
      showToast(getErrorMessage(patientDashboardFailure), 'failure');
      dispatch(resetPatientDashboardMessage());
    }
  }, [patientDashboardSuccess, patientDashboardFailure]);

  return (
    <div className="min-h-screen w-full bg-[#F5F7FA] mx-auto ">
      {/* header section */}
      <div className="py-8 flex flex-col gap-8">
        {/* title */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-FigtreeBold text-ui_blue text-center px-4">Find the Right Specialist for You</h1>

        {/* Search + Filters: responsive layout */}
        <div className="px-4 flex flex-col gap-4">
          <div className="w-full max-w-[500px] mx-auto lg:hidden flex items-center gap-3">
            <TextInput
              value={localSearchTerm}
              onChangeText={handleSearchChange}
              placeholder="Search by Specialist Name or Hospital Name"
              leftIcon="search_blue"
              iconClassName="ml-2"
              wrapperClassName="!bg-white border border-ui_input_border_dark/40 rounded-lg"
              className="flex-1"
            />
          </div>

          {/* Filters: stacked on small screens, flex-wrap on md+ */}
          <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:justify-center md:gap-4">
            {/* Experience — full width on small, fixed width on md+ */}
            <div className="w-full max-w-[200px] mx-auto md:mx-0 md:w-[200px]">
              <SelectInput
                value={filterExperience?.id as any}
                options={filterOptions.experienceFilters as SelectInputitem[]}
                onChange={item => setFilterExperience(item as FilterItemResponse)}
                wrapperClassName="!h-[44px] !bg-white w-full md:!w-[200px] border"
                className="w-full md:!w-[200px]"
                placeholder="Filter by Experience"
                textClassName="text-ui_input_border_dark"
              />
            </div>

            {/* Charges + Ratings — side‑by‑side on small, flex items on md+ */}
            <div className="grid grid-cols-2 gap-4 md:contents">
              <div className="w-full md:w-[200px]">
                <SelectInput
                  value={filterCharges?.id as any}
                  options={filterOptions.chargesFilters as SelectInputitem[]}
                  onChange={item => setFilterCharges(item as FilterItemResponse)}
                  wrapperClassName="!h-[44px] !bg-white w-full md:!w-[200px] border"
                  className="w-full md:!w-[200px]"
                  placeholder="Filter by Charges"
                  textClassName="text-ui_input_border_dark"
                />
              </div>
              <div className="w-full md:w-[200px]">
                <SelectInput
                  value={filterRatings?.id as any}
                  options={filterOptions.ratingFilters as SelectInputitem[]}
                  onChange={item => setFilterRatings(item as FilterItemResponse)}
                  wrapperClassName="!h-[44px] !bg-white w-full md:!w-[200px] border"
                  className="w-full md:!w-[200px]"
                  placeholder="Filter by Ratings"
                  textClassName="text-ui_input_border_dark"
                />
              </div>
            </div>

            {/* Reset + Apply — side‑by‑side on small when Reset shown, else Apply full width */}
            {hasActiveFilters ? (
              <div className="flex gap-4 justify-center self-center">
                <div className="w-full max-w-[200px] mx-auto md:mx-0 md:w-[200px]">
                  <Button onClick={handleResetFilters} className="xl:h-[44px] !w-full md:!w-[200px] px-5" title="Reset Filters" variant="secondary" />
                </div>
                <div className="w-full max-w-[200px] mx-auto md:mx-0 md:w-[200px]">
                  <Button onClick={handleApplyFilters} className="xl:h-[44px] !w-full md:!w-[200px] px-5" title="Apply Filters" />
                </div>
              </div>
            ) : (
              <div className="w-full min-w-[200px] max-w-[200px] mx-auto md:mx-0 md:w-[200px]">
                <Button onClick={handleApplyFilters} className="xl:h-[44px] !w-full md:!w-[200px] px-5" title="Apply Filters" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Browse by Category Section */}
      {specialization && specialization.length > 0 && (
        <div className="mb-20 bg-white py-5 ">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-FigtreeBold text-ui_blue text-center mb-6 ">Browse Specialists by Category</h2>

          <div className="flex flex-wrap justify-center gap-2 mb-4 px-2">
            {displayedCategories.map(category => {
              const isSelected = selectedCategory === category.id;
              return (
                // <div key={category.id} className={`p-[1px] rounded-full ${!isSelected ? 'bg-gradient-to-r from-ui_blue to-ui_blue_light' : ''}`}>
                <button
                  onClick={() => handleCategorySelect(category.id)}
                  className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm md:text-base transition-all ${
                    isSelected ? 'bg-ui_bg_light_blue font-FigtreeBold text-ui_blue' : 'bg-white font-FigtreeMedium text-ui_blue border border-ui_blue'
                  }`}>
                  <Text variant="small" className={cn(isSelected ? 'gradient-text' : '')}>
                    {category.label}
                  </Text>
                </button>
                // </div>
              );
            })}
          </div>

          {specialization.length > 6 && (
            <div className="text-center ">
              <button
                onClick={() => setShowAllCategories(!showAllCategories)}
                className="text-ui_blue/70 text-xs sm:text-sm md:text-base font-PoppinsSemiBold hover:underline">
                {showAllCategories ? 'See less' : 'See more'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Initial Loading State */}
      {isLoading && specialistList.length === 0 && (
        <div className="text-center py-12 px-4">
          <p className="text-[#4a6578] text-base sm:text-lg md:text-xl">Loading specialists...</p>
        </div>
      )}

      {/* Doctors Grid - FIXED */}
      {specialistList.length > 0 && (
        <>
          <div className="specialist-screen-grid px-4 mb-8 w-full justify-items-center">
            {specialistList.map((doctor, index) => (
              <SpecialistCard
                disabled={false}
                key={`${doctor.id}-${index}`}
                doctor={doctor}
                isSelected={doctor.id === selectedDoctorId}
                onSelect={handleSelectDoctor}
                onBookNow={handleBookNow}
                onHospitalBooking={handleHospitalBooking}
                isPopular={(specialistList[index] as any)?.popular || false}
                showBookNow={true}
                showFavoriteIcon={true}
                onFavoriteToggle={handleFavoriteToggle}
                lastConsultedDate={getLastConsultedDate(doctor.name)}
              />
            ))}
          </div>

          {/* Infinite Scroll Trigger */}
          <div ref={observerTarget} className="h-1" />
        </>
      )}

      {/* No Results - Show different messages based on context */}
      {!isLoading && specialistList.length === 0 && (
        <div className="text-center py-12 px-4 max-w-3xl mx-auto">
          {!specialization || specialization.length === 0 ? (
            <p className="text-ui_blue text-base sm:text-lg md:text-xl font-bold">Currently no specialist available, please try again in sometime.</p>
          ) : (
            (hasActiveFilters || searchTerm) && (
              <p className="text-ui_blue text-base sm:text-lg md:text-xl font-bold">No Specialists or Hospitals matched your search. Please try again later.</p>
            )
          )}
        </div>
      )}

      {/* Hospital Booking Modal */}
      {selectedHospital && (
        <HospitalBookingModal
          open={isHospitalBookingModalOpen}
          onClose={handleCloseHospitalModal}
          hospitalName={selectedHospital.name}
          hospitalId={selectedHospital.id}
          specializations={selectedHospital?.available_specializations || []}
          initialSelectedSpecialization={savedModalState?.returnPath === Routes.SPECIALISTS ? savedModalState?.selectedSpecialization ?? null : null}
          initialIsStep2={savedModalState?.returnPath === Routes.SPECIALISTS ? savedModalState?.isStep2 : false}
          onStateChange={data =>
            dispatch(
              updateHospitalBookingModalState({
                selectedSpecialization: data.selectedSpecialization,
                isStep2: data.isStep2,
              }),
            )
          }
        />
      )}
    </div>
  );
};

export {FavoritesScreen} from './FavoriteSpecialistScreen';
